import { DataGrid } from "@mui/x-data-grid";
import React, { useEffect, useRef, useState } from "react";
import editIcon from "../../../assets/images/Edit.svg";
import printIcon from "../../../assets/images/PrintIcon.svg";
import viewIcon from "../../../assets/images/View.svg";
import { getLoggedInUserIsAuditeeOrAuditor } from "../../../common/CommonFunctions";
import { ROLES } from "../../../config/Roles.config";
import { MuiDataGridFontSize, MuiDataGridStyle } from "../../../theme/App.theme";
import NoRecordsToDisplay from "../../../components/CommonReusableComponent/NoRecordsToDisplay/NoRecordsToDisplay";
import { getCompanyInformation } from "../../../helpers/api-functions-helper";
import { APP_MESSAGE, SNACKBAR_VARIANT } from "../../../config/Messages.config";
import { getUserRoleAndScreens } from "../../../services/authServices/auth.services";
import { useBackdrop } from "../../../contexts/BackDrop.context";
import { useSnackbar } from "../../../contexts/Snackbar.context";
import { auditStatusConfigForAuditMaster } from "../../../config/AuditStatusConfig";
import { Chip, Tooltip, Typography } from "@mui/material";
import {
  alertRangeForAudit,
  AuditStatusPill,
  getStatusChipDetails,
  warningRangeForAudit,
} from "./AuditMasterDataTable.config";
import PrintAuditComponent from "../../../components/PrintWorkPermitComponent/PrintAuditPreviewModal";
import { useReactToPrint } from "react-to-print";

const AuditMasterDataTable_V2 = ({
  Rows,
  GetRowDataOnClickOfAction,
  PageSizeOptions,
  RowsToDisplay,
}) => {
  const { accountId, role } = getUserRoleAndScreens();
  const { showBackdrop, hideBackdrop } = useBackdrop();
  const { showSnackbar } = useSnackbar();
  const contentRef = useRef(null);

  // Print state
  const [printPreview, setPrintPreview] = useState({
    data: null,
    isVisible: false,
  });

  // Company Details
  const [companyDetails, setCompanyDetails] = useState({});

  const [isPrinting, setIsPrinting] = useState(false);

  // We store the resolve Promise being used in `onBeforePrint` here
  const promiseResolveRef = useRef(null);

  // Function used to get company information
  const fetchCompanyInformation = () => {
    showBackdrop();
    getCompanyInformation(accountId)
      .then((response) => {
        if (response && response.data) {
          const { statusCode, data, message, success } = response.data;
          if (success && statusCode === 200) {
            setCompanyDetails(data[0]);
          } else {
            showSnackbar(
              message || data || APP_MESSAGE.COMPANY_MASTER.FETCHED.FAILED,
              SNACKBAR_VARIANT.ERROR
            );
          }
        }
      })
      .catch((error) => {
        console.log("Error: ", error);
        showSnackbar(
          APP_MESSAGE.COMPANY_MASTER.FETCHED.FAILED,
          SNACKBAR_VARIANT.ERROR
        );
      })
      .finally(() => {
        hideBackdrop();
      });
  };

  // -------- Conditions to show edit button ---------
  const getAuditee = (ID) => {
    return getLoggedInUserIsAuditeeOrAuditor(ROLES.AUDITEE, ID).isAuditee;
  };

  const getAuditor = (ID) => {
    return getLoggedInUserIsAuditeeOrAuditor(ROLES.AUDITOR, ID).isAuditor;
  };

  const isSuperAdmin =
    role && role.toLowerCase() === ROLES.SUPERADMIN ? true : false;

  const isAdmin = role && role.toLowerCase() === ROLES.ADMIN ? true : false;

  // Function used to open print preview of work permit
  const handlePrint = useReactToPrint({
    contentRef,
    onBeforePrint: () => {
      return new Promise((resolve) => {
        showBackdrop();
        document.title = `${
          printPreview?.data?.auditId
        }-${new Date().getTime()}`;
        promiseResolveRef.current = resolve;
        setIsPrinting(true);
      });
    },
    onAfterPrint: () => {
      // Reset the Promise resolve so we can print again
      promiseResolveRef.current = null;
      setIsPrinting(false);
      setPrintPreview((e) => ({
        ...e,
        data: null,
        isVisible: false,
      }));
      setCompanyDetails({});
      hideBackdrop();
    },
  });

  useEffect(() => {
    if (printPreview.isVisible && printPreview.data?.id) {
      fetchCompanyInformation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [printPreview]);

  useEffect(() => {
    if (Object.keys(companyDetails).includes("id")) {
      setTimeout(() => {
        handlePrint();
      }, 500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyDetails]);

  // We watch for the state to change here, and for the Promise resolve to be available
  useEffect(() => {
    if (isPrinting && promiseResolveRef.current) {
      // Resolves the Promise, letting `react-to-print` know that the DOM updates are completed
      promiseResolveRef.current();
      document.title = `${printPreview?.data?.auditId}-${new Date().getTime()}`;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPrinting]);

  // Data Table columns
  const Columns = [
    {
      field: "action",
      headerName: "Action",
      filterable: false,
      sortable: false,
      headerAlign: "center",
      disableColumnMenu: true,
      minWidth: 90,
      maxWidth: 90,
      renderCell: (param) => (
        <div className="d-flex gap-3 align-items-center h-100">
          <img
            src={viewIcon}
            className="cursor-pointer"
            alt={"View"}
            style={{ width: "20px", height: "20px" }}
            onClick={() => {
              GetRowDataOnClickOfAction(
                "View Audit",
                param?.row,
                "/audit-master/view"
              );
            }}
          />
          {((param?.row?.isAuditStarted &&
            (getAuditee(param?.row?.auditeeId) ||
              getAuditor(param?.row?.auditorId))) ||
            ((isSuperAdmin || isAdmin) &&
              param?.row?.status ===
                auditStatusConfigForAuditMaster.newAudits)) &&
            param?.row?.status !== auditStatusConfigForAuditMaster.closed && (
              <img
                src={editIcon}
                className="cursor-pointer"
                alt={"Edit"}
                style={{
                  width: "20px",
                  height: "20px",
                }}
                onClick={() => {
                  GetRowDataOnClickOfAction(
                    "Update Audit",
                    param?.row,
                    "/audit-master/update"
                  );
                }}
              />
            )}

          {param?.row.status === auditStatusConfigForAuditMaster.closed && (
            <img
              src={printIcon}
              className="cursor-pointer"
              alt={"Print"}
              style={{
                width: "20px",
                height: "20px",
              }}
              onClick={() => {
                setPrintPreview((prev) => ({
                  ...prev,
                  data: param?.row,
                  isVisible: true,
                }));
              }}
            />
          )}
        </div>
      ),
    },
    {
      field: "userRoleShortForm",
      headerName: "User Role",
      sortable: false,
      flex: 1,
      maxWidth: 80,
      minWidth: 80,
      renderCell: (param) => (
        <div className="d-flex justify-content-center align-items-center h-100">
          <Typography className="inter-medium-500">{param?.value}</Typography>
        </div>
      )
    },
    {
      field: "auditTypeCategoryName",
      headerName: "Audit Type Category",
      sortable: false,
      flex: 1,
      minWidth: 170,
    },
    {
      field: "auditTypeName",
      headerName: "Audit Type",
      sortable: false,
      flex: 1,
      minWidth: 170,
    },
    {
      field: "areaName",
      headerName: "Area",
      sortable: false,
      flex: 1,
      minWidth: 200,
    },
    {
      field: "status",
      headerName: "Status",
      sortable: false,
      flex: 1,
      minWidth: 100,
      maxWidth: 130,
      renderCell: (param) => <AuditStatusPill data={param?.value}/>
    },
    {
      field: "differenceInDays",
      headerName: "Day(s) Left",
      sortable: true,
      // type: 'number',
      flex: 1,
      minWidth: 100,
      maxWidth: 100,
      renderCell: (param) => <StatusPillForDaysLeft data={param?.value} />,
    },
    {
      field: "totalActivities",
      headerName: "Total Activities",
      sortable: false,
      flex: 1,
      minWidth: 100,
      maxWidth: 100,
    },
    {
      field: "totalNCs",
      headerName: "Total NC's",
      sortable: false,
      flex: 1,
      minWidth: 100,
      maxWidth: 100,
    },
    {
      field: "openNCs",
      headerName: "Open NC's",
      sortable: false,
      flex: 1,
      minWidth: 100,
      maxWidth: 100,
    },
    {
      field: "closedNCs",
      headerName: "Closed NC's",
      sortable: false,
      flex: 1,
      minWidth: 100,
      maxWidth: 100,
    },
    {
      field: "startAndEndDate",
      headerName: "Start and End Date",
      sortable: false,
      flex: 1,
      minWidth: 180,
    },
    // {
    //   field: "startDate",
    //   headerName: "Start Date",
    //   sortable: false,
    //   flex: 1,
    //   minWidth: 110,
    // },
    // {
    //   field: "endDate",
    //   headerName: "End Date",
    //   sortable: false,
    //   flex: 1,
    //   minWidth: 100,
    // },
  ];

  return (
    <React.Fragment>
      <div
        style={{
          display: "none",
        }}
      >
        <PrintAuditComponent
          ref={contentRef}
          CompanyInfo={companyDetails}
          PreviewData={printPreview.isVisible ? printPreview.data : {}}
        />
      </div>
      <div className="w-100">
        <DataGrid
          columns={Columns}
          rows={Rows}
          slots={{
            noRowsOverlay: NoRecordsToDisplay,
          }}
          pageSizeOptions={PageSizeOptions || [5, 10, 15, 25, 50]}
          initialState={{
            density: "compact",
            pagination: {
              paginationModel: {
                pageSize: RowsToDisplay || 10,
              },
            },
          }}
          disableRowSelectionOnClick
          density="compact"
          className="custom-data-grid"
          sx={MuiDataGridStyle}
        />
      </div>
    </React.Fragment>
  );
};

const StatusPillForDaysLeft = ({ data }) => {
  const getPillDetails = (daysLeft) => {
    if (alertRangeForAudit.includes(daysLeft)) {
      return {
        pillColor: "#FFEBEE",
        textColor: "#C62828",
      };
    } else if (warningRangeForAudit.includes(daysLeft)) {
      return {
        pillColor: "#E3E8F6",
        textColor: "#2749b0",
      };
    } else if (daysLeft > 10) {
      return {
        pillColor: "#E8F5E9",
        textColor: "#2E7D32",
      };
    }
  };

  return (
    <Chip
      style={{
        background: getPillDetails(data).pillColor,
        color: getPillDetails(data).textColor,
        fontSize: "12px",
        // fontSize: MuiDataGridFontSize,
        border: `1px solid ${getPillDetails(data).textColor}`,
        borderRadius: "3px",
        height: "20px",
      }}
      size="small"
      className="px-1"
      label={data}
    />
  );
};


export default AuditMasterDataTable_V2;
